import { useQueryClient } from '@tanstack/react-query'
import { type RowSelectionState, type VisibilityState } from '@tanstack/react-table'
import {
  Activity,
  BoltIcon,
  CheckCircle,
  ChevronDown,
  ClockIcon,
  MinusCircleIcon,
  PlusIcon,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { useLocalStorage } from 'usehooks-ts'

import SyncFiltersWithURL from 'components/admin/SyncFiltersWithUrl'
import TasksMonitoringView from 'components/admin/workflow_tasks/TasksMonitoringView'
import BulkUpdateTasksDialog from 'components/brokers/tasks/BulkUpdateTasksDialog'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { adminTasksTasks } from 'rails_routes'
import { Badge, Banner, Breadcrumbs, Button, Select } from 'slab'
import {
  getGetBrokersTasksTaskTypesTaskTypeHoldsQueryKey,
  useGetBrokersTasksTaskTypes,
  useGetBrokersTasksTaskTypesTaskTypeHolds,
  usePatchBrokersTasksV2HoldsHoldId,
  usePostBrokersTasksTaskTypesTaskTypeExecute,
  usePostBrokersTasksTaskTypesTaskTypeHolds,
} from 'utils/web_api'

// Filter configurations for URL syncing
type TaskV2ViewFilters = {
  statuses?: string[]
}

type TaskV2ViewFilterGroup = 'statuses'

const filterConfigurations: FilterConfigurations<TaskV2ViewFilters, TaskV2ViewFilterGroup> = {
  statuses: {
    key: 'statuses',
    label: 'Status',
    isMultiValue: true,
    group: 'statuses',
  },
}

// Column visibility mapping for tasks
const columnVisibilityMapping = {
  created_at: 'Created',
  status: 'Status',
  execution_method: 'Execution Method',
  workflow_status: 'Workflow Status',
  member_name: 'Member',
  carrier_name: 'Carrier',
  coverage_start_date: 'Coverage Start',
  assignees: 'Assignees',
  prerequisite_tasks: 'Prerequisite Tasks',
  dependent_tasks: 'Dependent Tasks',
}

const defaultColumnVisibility = {
  created_at: true,
  status: true,
  execution_method: true,
  workflow_status: true,
  member_name: true,
  carrier_name: true,
  coverage_start_date: true,
  assignees: true,
  prerequisite_tasks: true,
  dependent_tasks: true,
}

const MAX_NUMBER_OF_SELECTED_ITEMS = 100

const TaskV2View = () => {
  const navigate = useNavigate()
  const { taskType } = useParams<{ taskType: string }>()
  const queryClient = useQueryClient()

  // Decode the task type from URL
  const decodedTaskType = taskType ? decodeURIComponent(taskType) : undefined

  // Filter state for status filtering and date range
  const [filters, setFilters] = useState<TaskV2ViewFilters>({})
  const [pagination, _setPagination] = useState({ pageIndex: 1, pageSize: 20 })
  const [dateRange, setDateRange] = useState('30')

  const selectedStatuses = filters.statuses || []

  // Calculate date range filters
  const dateFilters = useMemo(() => {
    if (dateRange === 'all') {
      return {} // No date filters for "all tasks"
    }
    const days = parseInt(dateRange, 10)
    const afterDate = new Date()
    afterDate.setDate(afterDate.getDate() - days)
    return {
      after_date: afterDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
    }
  }, [dateRange])

  // Column visibility with localStorage persistence
  const [columnVisibilitySettings, setColumnVisibilitySettings] =
    useLocalStorage<VisibilityState>(
      // Same settings across all task types
      'task-type-column-visibility-settings',
      defaultColumnVisibility
    )

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    columnVisibilitySettings
  )

  // Row selection for bulk operations
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // State for actions menu and bulk edit dialog
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false)

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  // Sync column visibility changes to localStorage
  useEffect(() => {
    setColumnVisibilitySettings(columnVisibility)
  }, [columnVisibility, setColumnVisibilitySettings])

  // Guard against undefined taskType
  if (!decodedTaskType) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Banner type='critical' title='Invalid task type'>
          Task type is required
        </Banner>
      </div>
    )
  }

  // Fetch statistics using task_types endpoint
  const {
    data: taskTypeData,
    isLoading: isLoadingStats,
    error: statsError,
  } = useGetBrokersTasksTaskTypes({
    'task_types[]': [decodedTaskType],
    ...dateFilters,
  })

  // Extract statistics from the first (and only) task type result
  const taskStats = taskTypeData?.[0]?.task_statistics

  const {
    data: holds,
    isLoading: _isLoadingHolds,
    error: _holdsError,
  } = useGetBrokersTasksTaskTypesTaskTypeHolds(decodedTaskType)

  const [holdReason, setHoldReason] = useState('')
  const [showHoldDialog, setShowHoldDialog] = useState(false)

  // Hold management mutations
  const createHoldMutationOptions = {
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            getGetBrokersTasksTaskTypesTaskTypeHoldsQueryKey(decodedTaskType),
        })
        toast.success('Hold created successfully', {
          position: 'top-center',
        })
      },
    },
  }

  const clearHoldMutationOptions = {
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            getGetBrokersTasksTaskTypesTaskTypeHoldsQueryKey(decodedTaskType),
        })
        toast.success('Hold cleared successfully', {
          position: 'top-center',
        })
      },
    },
  }

  const createHoldMutation = usePostBrokersTasksTaskTypesTaskTypeHolds(
    createHoldMutationOptions
  )
  const clearHoldMutation = usePatchBrokersTasksV2HoldsHoldId(
    clearHoldMutationOptions
  )

  // Execute tasks mutation
  const executeTasksMutation = usePostBrokersTasksTaskTypesTaskTypeExecute({
    mutation: {
      onSuccess: () => {
        toast.success(
          'Task execution job queued. Automated tasks will be processed shortly.',
          {
            position: 'top-center',
          }
        )
      },
      onError: (error: any) => {
        toast.error(
          error?.message || 'Failed to queue task execution',
          {
            position: 'top-center',
          }
        )
      },
    },
  })

  // Navigation function to go back to list
  const onNavigateToList = () => {
    navigate(adminTasksTasks.spa())
  }

  // Get statistics from the endpoint data
  const getTaskStatusCount = (status: string): string => {
    if (isLoadingStats || !taskStats) return '--'
    return String(taskStats[status as keyof typeof taskStats] || 0)
  }

  const getTotalTasks = (): string => {
    if (isLoadingStats || !taskStats) return '--'
    return String(taskStats.total || 0)
  }

  // Toggle status filter when a card is clicked
  const toggleStatusFilter = (status: string) => {
    setFilters((prev) => {
      const currentStatuses = prev.statuses || []
      const newStatuses = currentStatuses.includes(status)
        ? currentStatuses.filter((s) => s !== status)
        : [...currentStatuses, status]

      return {
        ...prev,
        statuses: newStatuses.length > 0 ? newStatuses : undefined,
      }
    })
  }

  // Handle hold creation
  const handleCreateHold = () => {
    if (!holdReason.trim() || !decodedTaskType) {
      return
    }

    createHoldMutation.mutate({
      taskType: decodedTaskType,
      data: {
        reason: holdReason.trim(),
      },
    })

    setHoldReason('')
    setShowHoldDialog(false)
  }

  // Handle clearing a specific hold
  const handleClearHold = (holdId: string) => {
    clearHoldMutation.mutate({
      holdId,
      data: {
        cleared: true,
      },
    })
  }

  // Handle executing all automated tasks of this type
  const handleExecuteTasks = () => {
    if (!decodedTaskType) {
      return
    }

    executeTasksMutation.mutate({
      taskType: decodedTaskType,
    })
  }

  // Helper to format date for display
  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Get display name for the task type
  const displayName =
    decodedTaskType
      .split('::')
      .pop()
      ?.replace(/([A-Z])/g, ' $1')
      .trim() || 'Unknown Task Type'

  if (isLoadingStats) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <LoadingSpinnerCentered />
      </div>
    )
  }

  if (statsError) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Banner type='critical' title='Unable to load task statistics'>
          {statsError &&
          typeof statsError === 'object' &&
          'message' in statsError
            ? String((statsError as any).message)
            : 'An unknown error occurred'}
        </Banner>
      </div>
    )
  }

  const activeHolds = holds?.filter((hold) => hold.active) || []

  return (
    <div className='max-w-[2800px] mx-auto px-4 py-6 space-y-6'>
      <SyncFiltersWithURL
        filters={filters}
        setFilters={setFilters}
        pagination={pagination}
        filterConfigurations={filterConfigurations}
      />

      {/* Breadcrumb Navigation */}
      <Breadcrumbs>
        <Breadcrumbs.Item onClick={onNavigateToList}>
          Workflow Tasks
        </Breadcrumbs.Item>
        <Breadcrumbs.Item current>{displayName}</Breadcrumbs.Item>
      </Breadcrumbs>

      {/* Header with Date Range Filter */}
      <div className='flex flex-col gap-4'>
        <div className='flex justify-between items-start'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900'>{displayName}</h1>
            <Badge>{decodedTaskType}</Badge>
          </div>
          <div className='flex items-center gap-2'>
            <label htmlFor='date-range-select' className='text-sm font-medium text-gray-700'>
              Date Range:
            </label>
            <Select
              id='date-range-select'
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className='w-36'
            >
              <option value='7'>Last 7 days</option>
              <option value='14'>Last 14 days</option>
              <option value='30'>Last 30 days</option>
              <option value='all'>All tasks</option>
            </Select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex gap-2 justify-end'>
          <Button
            variant='tertiary-destructive'
            onClick={() => setFilters({})}
            disabled={selectedStatuses.length === 0}
          >
            Clear Filters
          </Button>
          <Button
            variant='secondary'
            onClick={() => setIsBulkUpdateOpen(true)}
            disabled={numberOfSelectedItems === 0 || numberOfSelectedItems > MAX_NUMBER_OF_SELECTED_ITEMS}
          >
            Bulk Edit ({numberOfSelectedItems})
          </Button>
          <DropdownMenu open={isActionsMenuOpen} onOpenChange={setIsActionsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='secondary'
                data-testid='actions-menu'
              >
                <div className='flex items-center'>
                  Actions
                  <ChevronDown className='ml-1' size={12} />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Table Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Columns</DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    {Object.entries(columnVisibilityMapping).map(
                      ([key, label]) => {
                        return (
                          <DropdownMenuCheckboxItem
                            key={key}
                            checked={
                              columnVisibility[
                                key as keyof typeof columnVisibility
                              ]
                            }
                            onCheckedChange={(checked) => {
                              setColumnVisibility((prev) => ({
                                ...prev,
                                [key]: checked,
                              }))
                            }}
                          >
                            {label}
                          </DropdownMenuCheckboxItem>
                        )
                      }
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant='primary-destructive'
            size='small'
            onClick={() => setShowHoldDialog(true)}
            disabled={createHoldMutation.isPending}
            rightIcon={activeHolds.length > 0 ? BoltIcon : PlusIcon}
          >
            {activeHolds.length > 0 ? `Manage Holds (${activeHolds.length})` : 'Add Hold'}
          </Button>
          <Button
            variant='primary'
            onClick={handleExecuteTasks}
            disabled={executeTasksMutation.isPending}
          >
            {executeTasksMutation.isPending
              ? 'Queueing...'
              : 'Execute Automated Tasks'}
          </Button>
        </div>
      </div>

      {/* Task Statistics */}
      <div className='flex flex-wrap justify-start gap-4'>
        <Card className='bg-white border border-purple-300 w-[250px]'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-purple-400 flex items-center gap-2 truncate'>
              <Activity className='w-4 h-4 flex-shrink-0' />
              Total Tasks
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div className='text-2xl font-bold text-purple-500'>
              {getTotalTasks()}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border border-green-300 cursor-pointer transition-all w-[250px] ${
            selectedStatuses.includes('completed')
              ? 'bg-green-300 shadow-lg'
              : 'bg-white hover:bg-green-50'
          } hover:shadow-md`}
          onClick={() => toggleStatusFilter('completed')}
        >
          <CardHeader className='pb-2'>
            <CardTitle
              className={`text-sm font-medium flex items-center gap-2 truncate transition-colors ${
                selectedStatuses.includes('completed')
                  ? 'text-green-800'
                  : 'text-green-400 hover:text-green-600'
              }`}
            >
              <CheckCircle className='w-4 h-4 flex-shrink-0' />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div
              className={`text-2xl font-bold transition-colors ${
                selectedStatuses.includes('completed')
                  ? 'text-green-800'
                  : 'text-green-500 hover:text-green-700'
              }`}
            >
              {getTaskStatusCount('completed')}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border border-yellow-300 cursor-pointer transition-all w-[250px] ${
            selectedStatuses.includes('pending')
              ? 'bg-yellow-300 shadow-lg'
              : 'bg-white hover:bg-yellow-50'
          } hover:shadow-md`}
          onClick={() => toggleStatusFilter('pending')}
        >
          <CardHeader className='pb-2'>
            <CardTitle
              className={`text-sm font-medium flex items-center gap-2 truncate transition-colors ${
                selectedStatuses.includes('pending')
                  ? 'text-yellow-800'
                  : 'text-yellow-400 hover:text-yellow-600'
              }`}
            >
              <ClockIcon className='w-4 h-4 flex-shrink-0' />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div
              className={`text-2xl font-bold transition-colors ${
                selectedStatuses.includes('pending')
                  ? 'text-yellow-800'
                  : 'text-yellow-500 hover:text-yellow-700'
              }`}
            >
              {getTaskStatusCount('pending')}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border border-blue-300 cursor-pointer transition-all w-[250px] ${
            selectedStatuses.includes('processing')
              ? 'bg-blue-300 shadow-lg'
              : 'bg-white hover:bg-blue-50'
          } hover:shadow-md`}
          onClick={() => toggleStatusFilter('processing')}
        >
          <CardHeader className='pb-2'>
            <CardTitle
              className={`text-sm font-medium flex items-center gap-2 truncate transition-colors ${
                selectedStatuses.includes('processing')
                  ? 'text-blue-800'
                  : 'text-blue-400 hover:text-blue-600'
              }`}
            >
              <Activity className='w-4 h-4 flex-shrink-0' />
              Processing
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div
              className={`text-2xl font-bold transition-colors ${
                selectedStatuses.includes('processing')
                  ? 'text-blue-800'
                  : 'text-blue-500 hover:text-blue-700'
              }`}
            >
              {getTaskStatusCount('processing')}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border border-red-300 cursor-pointer transition-all w-[250px] ${
            selectedStatuses.includes('failed')
              ? 'bg-red-300 shadow-lg'
              : 'bg-white hover:bg-red-50'
          } hover:shadow-md`}
          onClick={() => toggleStatusFilter('failed')}
        >
          <CardHeader className='pb-2'>
            <CardTitle
              className={`text-sm font-medium flex items-center gap-2 truncate transition-colors ${
                selectedStatuses.includes('failed')
                  ? 'text-red-800'
                  : 'text-red-400 hover:text-red-600'
              }`}
            >
              <XCircle className='w-4 h-4 flex-shrink-0' />
              Failed
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div
              className={`text-2xl font-bold transition-colors ${
                selectedStatuses.includes('failed')
                  ? 'text-red-800'
                  : 'text-red-500 hover:text-red-700'
              }`}
            >
              {getTaskStatusCount('failed')}
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border border-gray-300 cursor-pointer transition-all w-[250px] ${
            selectedStatuses.includes('excluded')
              ? 'bg-gray-300 shadow-lg'
              : 'bg-white hover:bg-gray-50'
          } hover:shadow-md`}
          onClick={() => toggleStatusFilter('excluded')}
        >
          <CardHeader className='pb-2'>
            <CardTitle
              className={`text-sm font-medium flex items-center gap-2 truncate transition-colors ${
                selectedStatuses.includes('excluded')
                  ? 'text-gray-800'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <MinusCircleIcon className='w-4 h-4 flex-shrink-0' />
              Excluded
            </CardTitle>
          </CardHeader>
          <CardContent className='pt-0'>
            <div
              className={`text-2xl font-bold transition-colors ${
                selectedStatuses.includes('excluded')
                  ? 'text-gray-800'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {getTaskStatusCount('excluded')}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Animated Hold Banner - shows above table when holds are active */}
      <AnimateHeightAndOpacity isOpen={activeHolds.length > 0}>
        <Banner type='critical' title={`${activeHolds.length} Active Hold${activeHolds.length !== 1 ? 's' : ''}`}>
          <div className='space-y-2 mt-2'>
            {activeHolds.map((hold) => (
              <div key={hold.id} className='flex justify-between items-start bg-red-100 p-2 rounded'>
                <div className='flex-1'>
                  <p className='font-medium'>{hold.reason}</p>
                  <p className='text-xs opacity-75'>
                    Created: {formatDateTime(hold.created_at)}
                  </p>
                </div>
                <Button
                  variant='tertiary-destructive'
                  size='small'
                  onClick={() => handleClearHold(hold.id)}
                  disabled={clearHoldMutation.isPending}
                >
                  Clear
                </Button>
              </div>
            ))}
          </div>
        </Banner>
      </AnimateHeightAndOpacity>

      {/* Hold Creation Dialog */}
      {showHoldDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto'>
            <h3 className='text-lg font-semibold mb-4'>
              {activeHolds.length > 0 ? 'Manage Holds' : 'Add Task Type Hold'}
            </h3>

            {/* Show current active hold in the dialog */}
            {activeHolds.length > 0 && (
              <div className='mb-4 space-y-2'>
                <h4 className='text-sm font-medium text-red-700'>Current Hold:</h4>
                {activeHolds.map((hold) => (
                  <div
                    key={hold.id}
                    className='flex justify-between items-start p-3 bg-red-50 rounded border border-red-200'
                  >
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-red-800'>{hold.reason}</p>
                      <p className='text-xs text-red-600'>
                        Created: {formatDateTime(hold.created_at)}
                      </p>
                    </div>
                    <Button
                      variant='tertiary-destructive'
                      size='small'
                      onClick={() => handleClearHold(hold.id)}
                      disabled={clearHoldMutation.isPending}
                    >
                      Clear
                    </Button>
                  </div>
                ))}
                <div className='border-t pt-3 mt-3'>
                  <h4 className='text-sm font-medium text-gray-700 mb-2'>Add Another Hold:</h4>
                </div>
              </div>
            )}

            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Reason for hold:
                </label>
                <textarea
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  rows={3}
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  placeholder='Explain why this task type needs to be put on hold...'
                />
              </div>
              <div className='flex gap-2 justify-end'>
                <Button
                  variant='tertiary'
                  onClick={() => {
                    setShowHoldDialog(false)
                    setHoldReason('')
                  }}
                >
                  {activeHolds.length > 0 ? 'Close' : 'Cancel'}
                </Button>
                <Button
                  variant='primary'
                  onClick={handleCreateHold}
                  disabled={!holdReason.trim() || createHoldMutation.isPending}
                >
                  {createHoldMutation.isPending ? 'Creating...' : 'Create Hold'}
                </Button>
              </div>
            </div>

            {/* Show historical (cleared) holds if any */}
            {holds && holds.filter((h) => !h.active).length > 0 && (
              <div className='mt-6 pt-6 border-t'>
                <h4 className='text-sm font-medium text-gray-700 mb-3'>
                  Historical Holds ({holds?.filter((h) => !h.active).length ?? 0})
                </h4>
                <div className='space-y-2 max-h-48 overflow-y-auto'>
                  {holds
                    ?.filter((h) => !h.active)
                    .map((hold) => (
                      <div
                        key={hold.id}
                        className='p-2 bg-gray-50 rounded border border-gray-200'
                      >
                        <p className='text-sm text-gray-700'>{hold.reason}</p>
                        <p className='text-xs text-gray-500 mt-1'>
                          Created: {formatDateTime(hold.created_at)}
                          {hold.cleared_at &&
                            ` • Cleared: ${formatDateTime(hold.cleared_at)}`}
                          {hold.cleared_by &&
                            ` by ${
                              hold.cleared_by.name || hold.cleared_by.email
                            }`}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tasks for this Task Type */}
      <Card className='bg-white'>
        <CardHeader>
          <CardTitle>Tasks of this Type</CardTitle>
        </CardHeader>
        <CardContent>
          <TasksMonitoringView
            taskTypes={[decodedTaskType!]}
            statusFilters={selectedStatuses}
            dateFilters={dateFilters}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
        </CardContent>
      </Card>

      {/* Bulk Update Dialog */}
      <BulkUpdateTasksDialog
        isOpen={isBulkUpdateOpen}
        setIsOpen={setIsBulkUpdateOpen}
        selectedItems={Object.keys(rowSelection).filter((key) => rowSelection[key])}
      />
    </div>
  )
}

export default TaskV2View
